import { useCallback, useRef } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import {
  ASSETS_COLLECTION_IDS_QUERY,
  ASSETS_COLLECTION_TABLE_QUERY,
} from "src/constants/graphql/queries";

export const useAssetsCollection = variables => {
  const client = useApolloClient();
  const data = useQuery(ASSETS_COLLECTION_TABLE_QUERY, {
    fetchPolicy: "network-only",
    variables,
  });

  const { loading, fetchMore } = data;
  const { metadata } = data?.data?.assetsCollection || {};

  // The table query no longer carries `totalIds` (140k+ ids per page view on
  // large tenants), so select-all asks for them on demand with the filters
  // currently in play. Callers must never read `metadata.totalIds` here — it is
  // always undefined.
  const variablesRef = useRef(variables);
  variablesRef.current = variables;

  const fetchTotalIds = useCallback(
    () =>
      client
        .query({
          query: ASSETS_COLLECTION_IDS_QUERY,
          variables: { ...variablesRef.current, page: 1, limit: 1 },
          fetchPolicy: "network-only",
        })
        .then(({ data: idsData }) => idsData?.assetsCollection?.metadata?.totalIds || []),
    [client]
  );

  const onLoadMore = () => {
    if (!metadata?.currentPage || loading) return;
    fetchMore({
      variables: { ...variables, page: metadata.currentPage + 1 },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        const existingIds = new Set(
          previousResult.assetsCollection.collection.map(item => item.id)
        );
        const newItems = fetchMoreResult.assetsCollection.collection.filter(
          item => !existingIds.has(item.id)
        );

        if (newItems.length === 0) return previousResult;

        return {
          ...previousResult,
          assetsCollection: {
            ...previousResult.assetsCollection,
            collection: [...previousResult.assetsCollection.collection, ...newItems],
            metadata: fetchMoreResult.assetsCollection.metadata,
          },
        };
      },
    });
  };

  return [data, { onLoadMore, fetchTotalIds }];
};
